import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import ClientMessagesTab from "@/components/client/ClientMessagesTab";
import {
  LogOut,
  Dog,
  Calendar,
  MessageSquare,
  Plus,
  Clock,
  ChevronRight,
  Bell,
  User,
  Home,
  GraduationCap,
  BookOpen,
  FileText,
  Trophy,
  TrendingUp,
  Download,
  Share2,
  ClipboardList,
  Video,
  Play,
  Archive
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
  const { user, logout, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [puppies, setPuppies] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [addPuppyOpen, setAddPuppyOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Selected enrollment for viewing details
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [reportCards, setReportCards] = useState([]);
  const [homework, setHomework] = useState([]);
  const [graduation, setGraduation] = useState(null);
  const [clientVideos, setClientVideos] = useState([]);
  const [archivedVideos, setArchivedVideos] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  
  const [newPuppy, setNewPuppy] = useState({
    name: "",
    breed: "",
    date_of_birth: "",
    vaccination_status: "",
    vaccination_notes: "",
    temperament_notes: "",
    pickup_address: "",
    special_requirements: ""
  });

  const fetchData = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const [puppiesRes, enrollmentsRes, notificationsRes, unreadRes, archivedRes] = await Promise.all([
        axios.get(`${API}/puppies`, { headers }),
        axios.get(`${API}/enrollments`, { headers }),
        axios.get(`${API}/notifications`, { headers }),
        axios.get(`${API}/messages/unread/count`, { headers }),
        axios.get(`${API}/client-videos/my-archived`, { headers }).catch(() => ({ data: [] }))
      ]);
      setPuppies(puppiesRes.data);
      setEnrollments(enrollmentsRes.data);
      setNotifications(notificationsRes.data.slice(0, 5));
      setUnreadCount(unreadRes.data.count);
      setArchivedVideos(archivedRes.data || []);
      
      // Auto-select first active enrollment
      const active = enrollmentsRes.data.find(e => ["confirmed", "in_progress", "completed"].includes(e.status));
      if (active) {
        await loadEnrollmentDetails(active);
      }
    } catch (err) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const loadEnrollmentDetails = async (enrollment) => {
    setSelectedEnrollment(enrollment);
    try {
      const headers = getAuthHeaders();
      const [reportsRes, homeworkRes, videosRes] = await Promise.all([
        axios.get(`${API}/report-cards/enrollment/${enrollment.id}`, { headers }),
        axios.get(`${API}/homework/enrollment/${enrollment.id}`, { headers }),
        axios.get(`${API}/client-videos/enrollment/${enrollment.id}`, { headers }).catch(() => ({ data: [] }))
      ]);
      setReportCards(reportsRes.data);
      setHomework(homeworkRes.data);
      setClientVideos(videosRes.data || []);
      
      // Check for graduation (new PDF certificate or old HTML)
      if (enrollment.status === "completed") {
        try {
          // Try new PDF certificate first
          const certRes = await axios.get(`${API}/graduation-certificates/enrollment/${enrollment.id}`, { headers });
          if (certRes.data && certRes.data.status === "finalized") {
            setGraduation({ ...certRes.data, isPdfCertificate: true });
          } else {
            // Fall back to old graduation system
            const gradRes = await axios.get(`${API}/graduations/enrollment/${enrollment.id}`, { headers });
            setGraduation(gradRes.data);
          }
        } catch {
          // Try old graduation system as fallback
          try {
            const gradRes = await axios.get(`${API}/graduations/enrollment/${enrollment.id}`, { headers });
            setGraduation(gradRes.data);
          } catch {
            setGraduation(null);
          }
        }
      } else {
        setGraduation(null);
      }
    } catch (err) {
      // Silent fail - loading details is supplementary
      console.debug('Non-critical error loading enrollment details:', err?.message);
    }
  };

  const handleAddPuppy = async (e) => {
    e.preventDefault();
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/puppies`, newPuppy, { headers });
      toast.success("Puppy added successfully!");
      setAddPuppyOpen(false);
      setNewPuppy({
        name: "",
        breed: "",
        date_of_birth: "",
        vaccination_status: "",
        vaccination_notes: "",
        temperament_notes: "",
        pickup_address: "",
        special_requirements: ""
      });
      fetchData();
    } catch (error) {
      toast.error("Failed to add puppy");
    }
  };

  const markHomeworkRead = async (homeworkId) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/homework/${homeworkId}/read`, {}, { headers });
      setHomework(prev => prev.map(hw => 
        hw.id === homeworkId ? { ...hw, is_read: true } : hw
      ));
    } catch (err) {
      // Silent fail - marking as read is non-critical
      console.debug('Non-critical error marking homework as read:', err?.message);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const activeEnrollments = enrollments.filter(e => 
    ["confirmed", "in_progress"].includes(e.status)
  );

  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed":
      case "completed": 
        return "bg-olive text-white";
      case "in_progress": 
        return "bg-olive/80 text-white";
      case "pending_payment": 
        return "bg-terracotta text-white";
      default: 
        return "bg-sand text-charcoal";
    }
  };

  const getScoreColor = (score) => {
    if (score >= 8) return "text-olive";
    if (score >= 6) return "text-charcoal";
    if (score >= 4) return "text-terracotta";
    return "text-stone";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-off-white flex items-center justify-center">
        <div className="animate-pulse text-olive font-heading text-2xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-off-white" data-testid="dashboard-page">
      {/* Header */}
      <header className="bg-white border-b border-olive/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex flex-col" data-testid="dashboard-logo">
            <span className="font-heading text-xl italic text-charcoal">The Pack Standard</span>
            <span className="text-olive text-xs tracking-wider">PUPPY ACADEMY</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/messages"
              className="relative p-2 hover:bg-sand rounded-full transition-colors"
              data-testid="messages-link"
            >
              <MessageSquare className="w-5 h-5 text-charcoal" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-terracotta text-white text-xs rounded-full flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-olive/10 flex items-center justify-center">
                <User className="w-5 h-5 text-olive" />
              </div>
              <div className="hidden md:block">
                <p className="font-medium text-charcoal text-sm">{user?.name}</p>
                <p className="text-stone text-xs">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 hover:bg-sand rounded-full transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5 text-charcoal" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-heading text-3xl md:text-4xl text-charcoal mb-2">
            Welcome back, {user?.name?.split(" ")[0]}
          </h1>
          <p className="text-stone">Manage your puppy and track their training progress.</p>
        </motion.div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-sand">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="videos" data-testid="tab-videos">Videos</TabsTrigger>
            <TabsTrigger value="homework" data-testid="tab-homework">Homework</TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages">Messages</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            {/* Quick Actions */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <Link to="/programs">
                <motion.div
                  whileHover={{ y: -4 }}
                  className="card-premium cursor-pointer text-center"
                  data-testid="quick-action-programs"
                >
                  <Calendar className="w-8 h-8 text-olive mx-auto mb-3" strokeWidth={1.5} />
                  <p className="font-medium text-charcoal">View Programs</p>
                </motion.div>
              </Link>
              <Link to="/messages">
                <motion.div
                  whileHover={{ y: -4 }}
                  className="card-premium cursor-pointer text-center"
                  data-testid="quick-action-messages"
                >
                  <MessageSquare className="w-8 h-8 text-olive mx-auto mb-3" strokeWidth={1.5} />
                  <p className="font-medium text-charcoal">Messages</p>
                  {unreadCount > 0 && (
                    <Badge className="mt-2 bg-terracotta">{unreadCount} new</Badge>
                  )}
                </motion.div>
              </Link>
              <Dialog open={addPuppyOpen} onOpenChange={setAddPuppyOpen}>
                <DialogTrigger asChild>
                  <motion.div
                    whileHover={{ y: -4 }}
                    className="card-premium cursor-pointer text-center"
                    data-testid="quick-action-add-puppy"
                  >
                    <Plus className="w-8 h-8 text-olive mx-auto mb-3" strokeWidth={1.5} />
                    <p className="font-medium text-charcoal">Add Puppy</p>
                  </motion.div>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">Add Your Puppy</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddPuppy} className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Puppy's Name</Label>
                        <Input
                          value={newPuppy.name}
                          onChange={(e) => setNewPuppy(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="e.g., Luna"
                          required
                          data-testid="puppy-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Breed</Label>
                        <Input
                          value={newPuppy.breed}
                          onChange={(e) => setNewPuppy(prev => ({ ...prev, breed: e.target.value }))}
                          placeholder="e.g., Labrador"
                          required
                          data-testid="puppy-breed-input"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Date of Birth</Label>
                        <Input
                          type="date"
                          value={newPuppy.date_of_birth}
                          onChange={(e) => setNewPuppy(prev => ({ ...prev, date_of_birth: e.target.value }))}
                          required
                          data-testid="puppy-dob-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Vaccination Status</Label>
                        <Select
                          value={newPuppy.vaccination_status}
                          onValueChange={(value) => setNewPuppy(prev => ({ ...prev, vaccination_status: value }))}
                        >
                          <SelectTrigger data-testid="puppy-vaccination-select">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="up_to_date">Up to Date</SelectItem>
                            <SelectItem value="partial">Partial</SelectItem>
                            <SelectItem value="pending">Pending</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Vaccination Notes (optional)</Label>
                      <Input
                        value={newPuppy.vaccination_notes}
                        onChange={(e) => setNewPuppy(prev => ({ ...prev, vaccination_notes: e.target.value }))}
                        placeholder="e.g., Next vaccination due 15 March"
                        data-testid="puppy-vax-notes-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Pickup Address</Label>
                      <Input
                        value={newPuppy.pickup_address}
                        onChange={(e) => setNewPuppy(prev => ({ ...prev, pickup_address: e.target.value }))}
                        placeholder="Full address for pickup"
                        required
                        data-testid="puppy-address-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Temperament Notes (optional)</Label>
                      <Textarea
                        value={newPuppy.temperament_notes}
                        onChange={(e) => setNewPuppy(prev => ({ ...prev, temperament_notes: e.target.value }))}
                        placeholder="e.g., Shy with new people, loves other dogs"
                        rows={2}
                        data-testid="puppy-temperament-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Special Requirements (optional)</Label>
                      <Textarea
                        value={newPuppy.special_requirements}
                        onChange={(e) => setNewPuppy(prev => ({ ...prev, special_requirements: e.target.value }))}
                        placeholder="Any special needs or requirements"
                        rows={2}
                        data-testid="puppy-special-input"
                      />
                    </div>
                    <Button type="submit" className="btn-primary w-full" data-testid="add-puppy-submit">
                      Add Puppy
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
              <Link to="/">
                <motion.div
                  whileHover={{ y: -4 }}
                  className="card-premium cursor-pointer text-center"
                  data-testid="quick-action-home"
                >
                  <Home className="w-8 h-8 text-olive mx-auto mb-3" strokeWidth={1.5} />
                  <p className="font-medium text-charcoal">Home</p>
                </motion.div>
              </Link>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              {/* My Puppies Section */}
              <div className="lg:col-span-2">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading text-2xl text-charcoal">My Puppy</h2>
                  <Dialog open={addPuppyOpen} onOpenChange={setAddPuppyOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" className="btn-outline py-2 px-4" data-testid="add-puppy-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Puppy
                      </Button>
                    </DialogTrigger>
                  </Dialog>
                </div>

                {puppies.length === 0 ? (
                  <div className="card-premium text-center py-12">
                    <Dog className="w-16 h-16 text-olive/50 mx-auto mb-4" strokeWidth={1.5} />
                    <h3 className="font-heading text-xl text-charcoal mb-2">No Puppy Yet</h3>
                    <p className="text-stone mb-4">Add your puppy to get started with our programs.</p>
                    <Button onClick={() => setAddPuppyOpen(true)} className="btn-primary" data-testid="add-first-puppy-btn">
                      Add Your First Puppy
                    </Button>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {puppies.map((puppy) => (
                      <Link to={`/puppies/${puppy.id}`} key={puppy.id}>
                        <motion.div
                          whileHover={{ y: -4 }}
                          className="card-premium cursor-pointer"
                          data-testid={`puppy-card-${puppy.id}`}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h3 className="font-heading text-xl text-charcoal">{puppy.name}</h3>
                              <p className="text-stone text-sm">{puppy.breed}</p>
                            </div>
                            <Badge className={
                              puppy.vaccination_status === "up_to_date" 
                                ? "bg-olive" 
                                : puppy.vaccination_status === "partial"
                                  ? "bg-terracotta"
                                  : "bg-sand text-charcoal"
                            }>
                              {puppy.vaccination_status.replace(/_/g, " ")}
                            </Badge>
                          </div>
                          <div className="flex gap-4 text-sm text-stone">
                            <span>{puppy.age_weeks} weeks old</span>
                          </div>
                          <div className="mt-4 flex items-center text-olive text-sm font-medium">
                            View Profile
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </div>
                        </motion.div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Enrollments & Notifications Section */}
              <div className="space-y-8">
                {/* Active Enrollments */}
                <div>
                  <h2 className="font-heading text-2xl text-charcoal mb-4">My Enrollments</h2>
                  
                  {enrollments.length === 0 ? (
                    <div className="card-premium text-center py-8">
                      <GraduationCap className="w-12 h-12 text-olive/50 mx-auto mb-3" strokeWidth={1.5} />
                      <p className="text-stone mb-3">No enrollments yet</p>
                      <Link to="/programs">
                        <Button className="btn-primary" data-testid="browse-programs-btn">Browse Programs</Button>
                      </Link>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {enrollments.map((enrollment) => (
                        <motion.div
                          key={enrollment.id}
                          whileHover={{ x: 4 }}
                          className={`card-premium p-4 cursor-pointer ${selectedEnrollment?.id === enrollment.id ? "border-olive border-2" : ""}`}
                          onClick={() => loadEnrollmentDetails(enrollment)}
                          data-testid={`enrollment-${enrollment.id}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-charcoal">{enrollment.puppy_name}</span>
                            <Badge className={getStatusColor(enrollment.status)}>
                              {enrollment.status.replace("_", " ")}
                            </Badge>
                          </div>
                          <p className="text-sm text-stone mb-1">{enrollment.program_name}</p>
                          <div className="flex items-center gap-2 text-sm text-stone">
                            <Clock className="w-4 h-4" />
                            {enrollment.day_of_week}s • Week {enrollment.sessions_completed}/4
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Recent Notifications */}
                <div>
                  <h2 className="font-heading text-2xl text-charcoal mb-4">Recent Updates</h2>
                  
                  {notifications.length === 0 ? (
                    <div className="card-premium text-center py-8">
                      <Bell className="w-12 h-12 text-olive/50 mx-auto mb-3" strokeWidth={1.5} />
                      <p className="text-stone">No notifications yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {notifications.map((notification) => (
                        <motion.div
                          key={notification.id}
                          className={`card-premium p-4 ${!notification.is_read ? "border-l-4 border-l-olive" : ""}`}
                          data-testid={`notification-${notification.id}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-full bg-olive/10 flex items-center justify-center shrink-0">
                              {notification.type === "report_card" ? (
                                <FileText className="w-4 h-4 text-olive" />
                              ) : notification.type === "homework" ? (
                                <BookOpen className="w-4 h-4 text-olive" />
                              ) : notification.type === "graduation" ? (
                                <GraduationCap className="w-4 h-4 text-olive" />
                              ) : (
                                <Bell className="w-4 h-4 text-olive" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-charcoal text-sm">{notification.title}</p>
                              <p className="text-stone text-xs mt-1">{notification.content}</p>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Videos Tab */}
          <TabsContent value="videos">
            {!selectedEnrollment ? (
              <div className="card-premium text-center py-12">
                <Video className="w-16 h-16 text-olive/50 mx-auto mb-4" />
                <h3 className="font-heading text-xl text-charcoal mb-2">No Enrollment Selected</h3>
                <p className="text-stone">Select an enrollment from the Overview tab to view training videos.</p>
              </div>
            ) : clientVideos.length === 0 && archivedVideos.length === 0 ? (
              <div className="card-premium text-center py-12">
                <Video className="w-16 h-16 text-olive/50 mx-auto mb-4" />
                <h3 className="font-heading text-xl text-charcoal mb-2">No Videos Yet</h3>
                <p className="text-stone">Training videos from your sessions will appear here.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Active Videos */}
                {clientVideos.length > 0 && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="font-heading text-2xl text-charcoal">Training Videos</h2>
                      <Badge className="bg-olive">{clientVideos.length} video{clientVideos.length !== 1 ? 's' : ''}</Badge>
                    </div>
                    {clientVideos.map((video) => (
                      <motion.div
                        key={video.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="card-premium"
                        data-testid={`video-${video.id}`}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className="bg-olive">Week {video.week_number}</Badge>
                              <h3 className="font-heading text-xl text-charcoal">{video.title}</h3>
                            </div>
                            {video.description && (
                              <p className="text-stone text-sm mt-1">{video.description}</p>
                            )}
                            <p className="text-stone text-xs mt-2">
                              Uploaded {new Date(video.created_at).toLocaleDateString("en-AU")}
                            </p>
                          </div>
                        </div>
                        
                        {/* Video Player */}
                        <div className="mt-4 rounded-lg overflow-hidden bg-charcoal">
                          <video
                            controls
                            className="w-full max-h-[400px]"
                            preload="metadata"
                            data-testid={`video-player-${video.id}`}
                          >
                            <source src={video.stream_url || `${process.env.REACT_APP_BACKEND_URL}${video.video_url}`} type="video/mp4" />
                            Your browser does not support the video tag.
                          </video>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}

                {/* Archived Videos Section */}
                {archivedVideos.length > 0 && (
                  <div className="space-y-4">
                    <button
                      onClick={() => setShowArchived(!showArchived)}
                      className="flex items-center gap-2 text-stone hover:text-charcoal transition-colors"
                      data-testid="toggle-archived-videos"
                    >
                      <Archive className="w-5 h-5" />
                      <span className="font-medium">
                        {showArchived ? "Hide" : "Show"} Archived Videos ({archivedVideos.length})
                      </span>
                      <ChevronRight className={`w-4 h-4 transition-transform ${showArchived ? "rotate-90" : ""}`} />
                    </button>
                    
                    {showArchived && (
                      <div className="space-y-4 mt-4 pl-4 border-l-2 border-stone/20">
                        <p className="text-stone text-sm italic">
                          These videos are from completed training programs. They remain fully accessible.
                        </p>
                        {archivedVideos.map((video) => {
                          const enrollment = enrollments.find(e => e.id === video.enrollment_id);
                          return (
                            <motion.div
                              key={video.id}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="card-premium bg-sand/30"
                              data-testid={`archived-video-${video.id}`}
                            >
                              <div className="flex items-start justify-between mb-4">
                                <div>
                                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    {enrollment && (
                                      <Badge variant="outline" className="text-stone">
                                        {enrollment.puppy_name}
                                      </Badge>
                                    )}
                                    <Badge className="bg-stone/50">Week {video.week_number}</Badge>
                                    <h3 className="font-heading text-lg text-charcoal">{video.title}</h3>
                                  </div>
                                  {video.description && (
                                    <p className="text-stone text-sm mt-1">{video.description}</p>
                                  )}
                                  <p className="text-stone text-xs mt-2">
                                    Uploaded {new Date(video.created_at).toLocaleDateString("en-AU")}
                                    {video.archived_at && ` • Archived ${new Date(video.archived_at).toLocaleDateString("en-AU")}`}
                                  </p>
                                </div>
                              </div>
                              
                              <div className="mt-4 rounded-lg overflow-hidden bg-charcoal">
                                <video
                                  controls
                                  className="w-full max-h-[400px]"
                                  preload="metadata"
                                >
                                  <source src={video.stream_url || `${process.env.REACT_APP_BACKEND_URL}${video.video_url}`} type="video/mp4" />
                                  Your browser does not support the video tag.
                                </video>
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* No active videos but has archived */}
                {clientVideos.length === 0 && archivedVideos.length > 0 && !showArchived && (
                  <div className="card-premium text-center py-8">
                    <Video className="w-12 h-12 text-olive/50 mx-auto mb-3" />
                    <p className="text-stone">No active videos for this enrollment.</p>
                    <p className="text-stone text-sm mt-1">Click "Show Archived Videos" above to view past training videos.</p>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* Homework Tab */}
          <TabsContent value="homework">
            {!selectedEnrollment ? (
              <div className="card-premium text-center py-12">
                <ClipboardList className="w-16 h-16 text-olive/50 mx-auto mb-4" />
                <h3 className="font-heading text-xl text-charcoal mb-2">No Enrollment Selected</h3>
                <p className="text-stone">Select an enrollment from the Overview tab to view homework.</p>
              </div>
            ) : homework.length === 0 ? (
              <div className="card-premium text-center py-12">
                <BookOpen className="w-16 h-16 text-olive/50 mx-auto mb-4" />
                <h3 className="font-heading text-xl text-charcoal mb-2">No Homework Yet</h3>
                <p className="text-stone">Homework assignments will appear here after each academy day.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {homework.map((hw) => (
                  <motion.div
                    key={hw.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`card-premium ${!hw.is_read ? "border-l-4 border-l-olive" : ""}`}
                    data-testid={`homework-${hw.id}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-heading text-xl text-charcoal">{hw.title}</h3>
                        <p className="text-olive text-sm">Week {hw.week_number} • {hw.focus_area.replace("_", " ")}</p>
                      </div>
                      <Badge className="bg-sand text-charcoal">
                        <Clock className="w-3 h-3 mr-1 inline" />
                        {hw.duration_minutes} min/day
                      </Badge>
                    </div>
                    
                    <div className="mb-4">
                      <p className="text-stone text-sm mb-2">Exercises:</p>
                      <ul className="space-y-2">
                        {hw.exercises.map((exercise, i) => (
                          <li key={`exercise-${hw.id}-${i}`} className="flex items-start gap-2">
                            <div className="w-5 h-5 rounded-full bg-olive/10 flex items-center justify-center shrink-0 mt-0.5">
                              <span className="text-olive text-xs">{i + 1}</span>
                            </div>
                            <span className="text-charcoal">{exercise}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {hw.tips && (
                      <div className="bg-sand/50 rounded-lg p-4">
                        <p className="text-stone text-sm font-medium mb-1">Tips</p>
                        <p className="text-charcoal text-sm">{hw.tips}</p>
                      </div>
                    )}

                    {/* File attachment download */}
                    {hw.file_url && (
                      <div className="mt-4">
                        <a 
                          href={`${process.env.REACT_APP_BACKEND_URL}${hw.file_url}`} 
                          target="_blank" 
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-olive text-white rounded-lg hover:bg-olive/90 text-sm"
                        >
                          <Download className="w-4 h-4" />
                          Download Resource
                        </a>
                      </div>
                    )}

                    {!hw.is_read && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-4"
                        onClick={() => markHomeworkRead(hw.id)}
                      >
                        Mark as Read
                      </Button>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <ClientMessagesTab getAuthHeaders={getAuthHeaders} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default DashboardPage;
